import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ArrowRight, TrendingUp, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getRentabilityBadge } from '@/lib/rentabilityBadge';
import { Badge } from '@/components/ui/badge';

export interface ComparaisonFinanciere {
  // Avant suppression (actuel)
  coutMOAvant: number;
  coutTotalAvant: number;
  margeAvant: number;
  rentaAvant: number;
  // Après suppression
  coutMOApres: number;
  coutTotalApres: number;
  margeApres: number;
  rentaApres: number;
  // Montant qui sera supprimé
  coutASupprimer: number;
  joursASupprimer: number;
  // Affichage ergonomique (temps calendaire + ouvriers)
  joursCalendaires: number;
  nbOuvriers: number;
}

interface TerminerStepConfirmSuppressionProps {
  chantierNom: string;
  comparaison: ComparaisonFinanciere;
  isLoading?: boolean;
  onAnnuler: () => void;      // Retour au screen précédent
  onConfirmer: () => void;    // Confirmer la suppression
}

// Formater un montant en euros
function formatMontant(montant: number): string {
  return montant.toLocaleString('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }) + ' €';
}

// Formater un pourcentage
function formatPct(pct: number): string {
  return pct.toFixed(1) + '%';
}

// Formater le temps calendaire de manière ergonomique
// Affiche: "X jours calendaires · Y ouvriers mobilisés"
function formatTempsCalendaire(joursCalendaires: number, nbOuvriers: number): string {
  // Formater le temps
  let tempsStr: string;
  if (joursCalendaires === 0) {
    tempsStr = '0 jour';
  } else if (joursCalendaires === 0.5) {
    tempsStr = 'une demi-journée';
  } else if (joursCalendaires === 1) {
    tempsStr = '1 jour calendaire';
  } else if (joursCalendaires % 1 === 0) {
    tempsStr = `${joursCalendaires} jours calendaires`;
  } else if (joursCalendaires % 1 === 0.5) {
    const entier = Math.floor(joursCalendaires);
    if (entier === 0) {
      tempsStr = 'une demi-journée';
    } else {
      tempsStr = `${entier} jour${entier > 1 ? 's' : ''} et demi`;
    }
  } else {
    tempsStr = `${joursCalendaires.toFixed(1)} jours calendaires`;
  }

  // Ajouter le nombre d'ouvriers
  if (nbOuvriers > 1) {
    return `${tempsStr} · ${nbOuvriers} ouvriers mobilisés`;
  } else if (nbOuvriers === 1) {
    return `${tempsStr} · 1 ouvrier mobilisé`;
  }

  return tempsStr;
}

export function TerminerStepConfirmSuppression({
  chantierNom,
  comparaison,
  isLoading = false,
  onAnnuler,
  onConfirmer,
}: TerminerStepConfirmSuppressionProps) {
  const badgeAvant = getRentabilityBadge(comparaison.rentaAvant);
  const badgeApres = getRentabilityBadge(comparaison.rentaApres);

  // Calculer l'amélioration
  const ameliorationMarge = comparaison.margeApres - comparaison.margeAvant;
  const ameliorationRenta = comparaison.rentaApres - comparaison.rentaAvant;

  return (
    <div className="space-y-6">
      {/* Titre avec alerte */}
      <div className="flex items-start gap-4 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
        <AlertTriangle className="w-6 h-6 text-orange-500 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-lg text-orange-600 dark:text-orange-400">
            Confirmation de suppression
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Vous êtes sur le point de retirer{' '}
            <strong className="text-orange-600 dark:text-orange-400">
              {formatTempsCalendaire(comparaison.joursCalendaires, comparaison.nbOuvriers)}
            </strong>{' '}
            de travail planifié du chantier "{chantierNom}".
          </p>
        </div>
      </div>

      {/* Montant à supprimer - mis en évidence */}
      <Card className="border-2 border-orange-500/50 bg-orange-500/5">
        <CardContent className="p-4 text-center">
          <p className="text-sm text-muted-foreground mb-1">Coûts à retirer du chantier</p>
          <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
            - {formatMontant(comparaison.coutASupprimer)}
          </p>
        </CardContent>
      </Card>

      {/* Tableau comparatif */}
      <Card>
        <CardContent className="p-4">
          <h4 className="font-medium mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
            Impact sur la rentabilité
          </h4>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 font-medium text-muted-foreground"></th>
                  <th className="text-right py-2 font-medium text-muted-foreground">Avant</th>
                  <th className="text-center py-2 w-8"></th>
                  <th className="text-right py-2 font-medium text-green-600">Après suppression</th>
                </tr>
              </thead>
              <tbody>
                {/* Coût Main d'Oeuvre */}
                <tr className="border-b border-border/50">
                  <td className="py-3">Coût Main d'Oeuvre</td>
                  <td className="py-3 text-right font-mono">{formatMontant(comparaison.coutMOAvant)}</td>
                  <td className="py-3 text-center">
                    <ArrowRight className="w-4 h-4 text-muted-foreground inline" />
                  </td>
                  <td className="py-3 text-right font-mono text-green-600">{formatMontant(comparaison.coutMOApres)}</td>
                </tr>

                {/* Marge */}
                <tr className="border-b border-border/50">
                  <td className="py-3">Marge</td>
                  <td className="py-3 text-right font-mono">{formatMontant(comparaison.margeAvant)}</td>
                  <td className="py-3 text-center">
                    <ArrowRight className="w-4 h-4 text-muted-foreground inline" />
                  </td>
                  <td className="py-3 text-right font-mono text-green-600">
                    {formatMontant(comparaison.margeApres)}
                    <span className="text-xs ml-1">(+{formatMontant(ameliorationMarge)})</span>
                  </td>
                </tr>

                {/* Rentabilité */}
                <tr>
                  <td className="py-3">Rentabilité</td>
                  <td className="py-3 text-right">
                    <Badge className={cn('font-mono', badgeAvant.bgColor, badgeAvant.color)}>
                      {formatPct(comparaison.rentaAvant)}
                    </Badge>
                  </td>
                  <td className="py-3 text-center">
                    <ArrowRight className="w-4 h-4 text-muted-foreground inline" />
                  </td>
                  <td className="py-3 text-right">
                    <Badge className={cn('font-mono', badgeApres.bgColor, badgeApres.color)}>
                      {formatPct(comparaison.rentaApres)}
                    </Badge>
                    <span className="text-xs text-green-600 ml-1">(+{ameliorationRenta.toFixed(1)} pts)</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Avertissement */}
      <p className="text-xs text-muted-foreground text-center">
        Cette action supprimera les jours planifiés après aujourd'hui du planning et des coûts.
        <br />
        Les jours déjà travaillés ne seront pas affectés.
      </p>

      {/* Boutons d'action */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1"
          onClick={onAnnuler}
          disabled={isLoading}
        >
          Annuler
        </Button>
        <Button
          variant="default"
          className="flex-1 bg-orange-500 hover:bg-orange-600"
          onClick={onConfirmer}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Suppression...
            </>
          ) : (
            'Confirmer la suppression'
          )}
        </Button>
      </div>
    </div>
  );
}
